import { useAuth } from '../../shared/auth/AuthContext';
import {
  useCurrentTenant,
  useOrganizationStats,
} from './useOrganization';
import {
  Card,
  CardBody,
  CardHeader,
  Badge,
  LoadingSkeleton,
  Button,
} from '@nexora/design-system';
import { useNavigate } from '@tanstack/react-router';

/* Inline SVG icons — avoids dependency on lucide-react */
const Building2Icon = (): JSX.Element => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="7" width="18" height="14" rx="2" />
    <path d="M7 11h2v2H7z" />
    <path d="M11 11h2v2h-2z" />
    <path d="M15 11h2v2h-2z" />
    <path d="M7 3h10v4H7z" />
    <path d="M9 3v1" />
    <path d="M15 3v1" />
    <path d="M7 17h10v4H7z" />
  </svg>
);

const StoreIcon = (): JSX.Element => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 3h16l2 4H2z" />
    <path d="M2 7v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V7" />
    <path d="M6 11v8a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-8" />
    <path d="M10 15h4" />
  </svg>
);

const HashIcon = (): JSX.Element => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="4" y1="9" x2="20" y2="9" />
    <line x1="4" y1="15" x2="20" y2="15" />
    <line x1="10" y1="3" x2="8" y2="21" />
    <line x1="16" y1="3" x2="14" y2="21" />
  </svg>
);

const ArrowRightIcon = (): JSX.Element => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

interface SubPageLink {
  title: string;
  description: string;
  icon: JSX.Element;
  path: string;
  count?: number;
}

export function OrganizationPage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';
  const navigate = useNavigate();

  const {
    data: tenantData,
    isLoading: tenantLoading,
    error: tenantError,
  } = useCurrentTenant(tenantId);

  const {
    data: stats,
    isLoading: statsLoading,
  } = useOrganizationStats(tenantId);

  const isLoading = tenantLoading || statsLoading;

  const subPages: SubPageLink[] = [
    {
      title: 'Empresas',
      description: 'Gerencie as empresas vinculadas ao tenant.',
      icon: <Building2Icon />,
      path: '/organization/companies',
      count: stats?.companiesCount,
    },
    {
      title: 'Filiais',
      description: 'Gerencie as filiais de cada empresa.',
      icon: <StoreIcon />,
      path: '/organization/branches',
      count: stats?.branchesCount,
    },
    {
      title: 'Sequencias Numericas',
      description: 'Configure padroes de numeracao para documentos fiscais e comerciais.',
      icon: <HashIcon />,
      path: '/organization/number-sequences',
      count: stats?.numberSequencesCount,
    },
  ];

  function handleNavigate(path: string): void {
    navigate({ to: path });
  }

  /* ── Error state ── */
  if (tenantError && !isLoading) {
    return (
      <div className="org-page">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
          <div>
            <h1 className="font-display-lg text-display-lg text-on-surface m-0">Organização</h1>
            <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Tenant, empresas e filiais.</p>
          </div>
        </div>
        <Card variant="elevated" padding="2rem">
          <CardBody>
            <div style={{ textAlign: 'center', color: '#ba1a1a' }}>
              <p style={{ fontWeight: 600, margin: '0 0 0.5rem', fontSize: '1rem' }}>
                Erro ao carregar dados do tenant
              </p>
              <p style={{ fontSize: '0.875rem', margin: 0, color: '#74777f' }}>
                {tenantError instanceof Error ? tenantError.message : 'Erro desconhecido'}
              </p>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  /* ── Main render ── */
  return (
    <div className="org-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Organização</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Tenant, empresas e filiais.</p>
        </div>
      </div>

      {/* Tenant card */}
      <Card variant="elevated" padding="1.5rem" className="org-tenant-card">
        <CardHeader>
          <h3
            style={{
              margin: 0,
              fontSize: '1rem',
              fontWeight: 600,
              color: '#191c1e',
            }}
          >
            Tenant
          </h3>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                color: '#43474e',
                padding: '0.5rem 0',
              }}
            >
              <LoadingSkeleton />
              <span style={{ fontSize: '0.875rem' }}>Carregando informacoes do tenant...</span>
            </div>
          ) : tenantData ? (
            <div className="org-tenant-info">
              <div className="org-tenant-field">
                <span className="org-tenant-label">Nome</span>
                <span className="org-tenant-value">{tenantData.name}</span>
              </div>
              <div className="org-tenant-field">
                <span className="org-tenant-label">Slug</span>
                <span className="org-tenant-value org-tenant-slug">{tenantData.slug}</span>
              </div>
              <div className="org-tenant-field">
                <span className="org-tenant-label">Status</span>
                <Badge
                  variant={tenantData.status === 'active' ? 'success' : 'warning'}
                  size="sm"
                >
                  {tenantData.status === 'active' ? 'Ativo' : tenantData.status}
                </Badge>
              </div>
              <div className="org-tenant-field">
                <span className="org-tenant-label">Criado em</span>
                <span className="org-tenant-value">
                  {new Date(tenantData.createdAt).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  })}
                </span>
              </div>
            </div>
          ) : (
            <p style={{ color: '#74777f', fontSize: '0.875rem', margin: 0 }}>
              Nenhum tenant encontrado.
            </p>
          )}
        </CardBody>
      </Card>

      {/* Sub-page grid */}
      <div className="org-grid">
        {subPages.map((page) => (
          <div
            key={page.path}
            className="org-grid-card-wrapper"
            onClick={() => handleNavigate(page.path)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleNavigate(page.path);
              }
            }}
            role="button"
            tabIndex={0}
          >
            <Card variant="elevated" padding="1.5rem" interactive>
              <CardBody>
                <div className="org-grid-card-content">
                  <div className="org-grid-icon">{page.icon}</div>
                  <div className="org-grid-text">
                    <h4 className="org-grid-title">{page.title}</h4>
                    <p className="org-grid-desc">{page.description}</p>
                  </div>
                  <div className="org-grid-footer">
                    {statsLoading ? (
                      <LoadingSkeleton />
                    ) : (
                      <span className="org-grid-count">
                        {page.count != null ? `${page.count} registros` : '—'}
                      </span>
                    )}
                    <Button variant="ghost" size="sm" className="org-grid-action">
                      <ArrowRightIcon />
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        ))}
      </div>

      <style>{`
        .org-page {
          padding: 1.5rem;
        }
        .org-tenant-card {
          margin-bottom: 1.5rem;
        }
        .org-tenant-info {
          display: flex;
          flex-wrap: wrap;
          gap: 1.25rem 2rem;
          padding: 0.25rem 0;
        }
        .org-tenant-field {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          min-width: 140px;
        }
        .org-tenant-label {
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #43474e;
          font-weight: 600;
        }
        .org-tenant-value {
          font-size: 0.9375rem;
          color: #191c1e;
          font-weight: 500;
        }
        .org-tenant-slug {
          font-family: monospace;
          font-size: 0.875rem;
          background: #f2f4f6;
          padding: 0.125rem 0.5rem;
          border-radius: 4px;
          display: inline-block;
        }
        .org-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1rem;
        }
        .org-grid-card-wrapper {
          cursor: pointer;
          outline: none;
        }
        .org-grid-card-wrapper:focus-visible {
          outline: 2px solid #002045;
          outline-offset: 2px;
          border-radius: 8px;
        }
        .org-grid-card-content {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        .org-grid-icon {
          color: #002045;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 44px;
          height: 44px;
          border-radius: 8px;
          background: #d6e3ff;
        }
        .org-grid-text {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        .org-grid-title {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
          color: #191c1e;
        }
        .org-grid-desc {
          margin: 0;
          font-size: 0.8125rem;
          color: #43474e;
          line-height: 1.4;
        }
        .org-grid-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 0.25rem;
        }
        .org-grid-count {
          font-size: 0.8125rem;
          color: #74777f;
          font-weight: 500;
        }
        .org-grid-action {
          color: #002045;
        }
        @media (max-width: 640px) {
          .org-grid {
            grid-template-columns: 1fr;
          }
          .org-tenant-info {
            flex-direction: column;
            gap: 0.75rem;
          }
        }
      `}</style>
    </div>
  );
}